import { __rest, __awaiter } from 'tslib';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import clsx from 'clsx';
import { Button as Button$1 } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../Button/Button.js';
import { Icon } from '../../Icon/Icon.js';
import { Stepper, StepperPanel } from '../../Stepper/Stepper.js';
import { ToastContent } from '../../Toast/ToastContent.js';
import { DialogSize } from '../constraints.js';
import { StepperDialogStepsContext, StepIndexProvider, StepperDialogContext } from '../contexts/StepperDialogContext.js';
import { CloseConfirmationDialog } from './CloseConfirmationDialog.js';
import { DialogWithDoubleEscape, useDialogClose } from './DialogWithDoubleEscape.js';

function StepperDialog(_ref) {
  let {
    steps,
    title,
    subtitle,
    allStepsReached,
    linear = true,
    onClose,
    size = DialogSize.md,
    loading,
    setLoading,
    pt,
    buttons,
    dialogClassName: propsDialogClassName,
    confirmClose,
    wrapper: Wrapper = _ref2 => {
      let {
        children
      } = _ref2;
      return jsx(Fragment, {
        children: children
      });
    }
  } = _ref;
  var _a;
  const [dialogSteps, setDialogSteps] = useState(steps);
  const [activeStep, setActiveStep] = useState(0);
  const [maxReachedStep, setMaxReachedStep] = useState(linear ? allStepsReached ? dialogSteps.length - 1 : 0 : dialogSteps.length - 1);
  const [completedMap, setCompletedMap] = useState(() => {
    if (!linear || !allStepsReached) {
      return {};
    }
    return Array.from({
      length: Math.max(dialogSteps.length - 1, 0)
    }, (_, index) => index).reduce((acc, index) => {
      acc[index] = true;
      return acc;
    }, {});
  });
  const [disabledMap, setDisabledMap] = useState({});
  const [invalidSteps, setInvalidSteps] = useState([]);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const handlersRef = useRef({});
  const toastRef = useRef(null);
  const skipNextToastClearRef = useRef(false);
  const stepEverValidRef = useRef(new Set());
  const isSubmittingRef = useRef(false);
  useEffect(() => {
    setDialogSteps(steps);
  }, [steps]);
  useEffect(() => {
    const lastStepIndex = Math.max(dialogSteps.length - 1, 0);
    setActiveStep(prev => Math.min(prev, lastStepIndex));
    setMaxReachedStep(prev => Math.min(prev, lastStepIndex));
    handlersRef.current = Object.fromEntries(Object.entries(handlersRef.current).filter(_ref3 => {
      let [k] = _ref3;
      return Number(k) < dialogSteps.length;
    }));
    setCompletedMap(prev => Object.fromEntries(Object.entries(prev).filter(_ref4 => {
      let [k] = _ref4;
      return Number(k) < dialogSteps.length;
    })));
    setDisabledMap(prev => Object.fromEntries(Object.entries(prev).filter(_ref5 => {
      let [k] = _ref5;
      return Number(k) < dialogSteps.length;
    })));
    setInvalidSteps(prev => prev.filter(i => i < dialogSteps.length));
  }, [dialogSteps.length]);
  const goToStep = useCallback((index, options) => {
    if (options === null || options === void 0 ? void 0 : options.keepToast) {
      skipNextToastClearRef.current = true;
    }
    setActiveStep(index);
    setMaxReachedStep(prev => Math.max(prev, index));
  }, []);
  const isLastStep = activeStep === dialogSteps.length - 1;
  const primaryDisabled = !!disabledMap[activeStep];
  const allPreviousStepsCompleted = dialogSteps.slice(0, -1).every((_, index) => completedMap[index] === true);
  const allStepsCompleted = linear && !isLastStep && maxReachedStep >= dialogSteps.length - 1 && allPreviousStepsCompleted && dialogSteps.every((_, index) => disabledMap[index] === false);
  const invalidStepSet = useMemo(() => new Set(invalidSteps), [invalidSteps]);
  const nextAccessibleIndex = activeStep + 1;
  const showNextAccessible = linear && !primaryDisabled && nextAccessibleIndex < dialogSteps.length;
  const canNavigateToStep = useCallback(targetIndex => {
    if (!linear) {
      return true;
    }
    if (targetIndex <= activeStep) {
      return true;
    }
    if (primaryDisabled) {
      return false;
    }
    const isImmediateNextStep = targetIndex === activeStep + 1;
    const isReachedForwardStep = targetIndex <= maxReachedStep;
    return isImmediateNextStep || isReachedForwardStep;
  }, [linear, activeStep, primaryDisabled, maxReachedStep]);
  const setStepCompleted = useCallback((index, completed) => {
    setCompletedMap(prev => {
      if (completed) {
        if (prev[index] === true) {
          return prev;
        }
        return Object.assign(Object.assign({}, prev), {
          [index]: true
        });
      }
      if (!(index in prev)) {
        return prev;
      }
      const _a = prev,
        _b = index;
        _a[_b];
        const rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
      return rest;
    });
  }, []);
  const getStepHeaderState = useCallback(index => {
    if (!linear) {
      return {
        reached: true,
        completed: false,
        nextAccessible: false,
        invalid: invalidStepSet.has(index),
        disabled: false,
        canInteract: true
      };
    }
    const reached = index <= maxReachedStep;
    const nextAccessible = showNextAccessible && index === nextAccessibleIndex;
    const canInteract = reached || nextAccessible;
    return {
      reached,
      completed: completedMap[index] === true,
      nextAccessible,
      invalid: invalidStepSet.has(index),
      disabled: !canInteract,
      canInteract
    };
  }, [linear, invalidStepSet, maxReachedStep, showNextAccessible, nextAccessibleIndex, completedMap]);
  const getSeparatorState = useCallback(index => {
    if (!linear) {
      return {
        reached: true,
        nextAccessible: false
      };
    }
    const targetStepIndex = index + 1;
    return {
      reached: targetStepIndex <= maxReachedStep,
      nextAccessible: showNextAccessible && targetStepIndex === nextAccessibleIndex
    };
  }, [linear, maxReachedStep, showNextAccessible, nextAccessibleIndex]);
  const registerProceed = useCallback((handler, index) => {
    handlersRef.current[index] = handler;
  }, []);
  const setPrimaryDisabled = useCallback((disabled, index, options) => {
    if (disabled) {
      setStepCompleted(index, false);
    }
    setDisabledMap(prev => {
      if (prev[index] === disabled) {
        return prev;
      }
      return Object.assign(Object.assign({}, prev), {
        [index]: disabled
      });
    });
    const isInvalid = options === null || options === void 0 ? void 0 : options.isInvalid;
    if (!disabled) {
      stepEverValidRef.current.add(index);
      setInvalidSteps(prev => prev.filter(s => s !== index));
    } else if (isInvalid === false) {
      setInvalidSteps(prev => prev.filter(s => s !== index));
    } else if (isInvalid === true || stepEverValidRef.current.has(index)) {
      setInvalidSteps(prev => prev.includes(index) ? prev : [...prev, index]);
    }
  }, [setStepCompleted]);
  const showToast = useCallback(message => {
    var _a;
    (_a = toastRef.current) === null || _a === void 0 ? void 0 : _a.show(Object.assign({
      content: ToastContent
    }, message));
  }, []);
  useEffect(() => {
    var _a;
    if (skipNextToastClearRef.current) {
      skipNextToastClearRef.current = false;
      return;
    }
    (_a = toastRef.current) === null || _a === void 0 ? void 0 : _a.clear();
  }, [activeStep]);
  const showStepInvalidToast = useCallback(() => {
    showToast({
      severity: 'error',
      detail: 'Please complete all required fields before continuing.',
      life: 6000
    });
  }, [showToast]);
  const waitForProceedHandler = useCallback(index => __awaiter(this, void 0, void 0, function* () {
    const timeoutMs = 1000;
    const pollIntervalMs = 20;
    const start = Date.now();
    while (Date.now() - start <= timeoutMs) {
      const handler = handlersRef.current[index];
      if (handler) {
        return handler;
      }
      yield new Promise(resolve => {
        setTimeout(resolve, pollIntervalMs);
      });
    }
    return undefined;
  }), []);
  const handleStepHeaderClickCapture = useCallback(event => {
    if (!linear) {
      return;
    }
    const target = event.target;
    const header = target.closest('.p-stepper-header');
    if (!header) {
      return;
    }
    const targetIndexValue = header.getAttribute('data-ics-step-index');
    const targetIndex = targetIndexValue === null ? NaN : Number(targetIndexValue);
    if (Number.isNaN(targetIndex)) {
      return;
    }
    if (!canNavigateToStep(targetIndex)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [linear, canNavigateToStep]);
  const handleProceed = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setLoading(true);
    try {
      if (!linear) {
        const invalidStepIndexes = [];
        for (let index = 0; index < dialogSteps.length - 1; index += 1) {
          const handler = handlersRef.current[index];
          if (!handler) {
            continue;
          }
          const isStepValid = yield handler();
          if (!isStepValid) {
            invalidStepIndexes.push(index);
          }
        }
        if (invalidStepIndexes.length > 0) {
          const firstInvalidStepIndex = invalidStepIndexes[0];
          setInvalidSteps(invalidStepIndexes);
          goToStep(firstInvalidStepIndex, {
            keepToast: true
          });
          showStepInvalidToast();
          return;
        }
        setInvalidSteps([]);
        const lastStepIndex = dialogSteps.length - 1;
        let submitHandler = handlersRef.current[lastStepIndex];
        if (!submitHandler) {
          goToStep(lastStepIndex, {
            keepToast: true
          });
          submitHandler = yield waitForProceedHandler(lastStepIndex);
        }
        if (!submitHandler) {
          showToast({
            severity: 'error',
            detail: 'Unable to submit. Please try again.',
            life: 6000
          });
          return;
        }
        const submitResult = yield submitHandler();
        if (submitResult) {
          onClose();
        }
        return;
      }
      const handler = handlersRef.current[activeStep];
      if (!handler) {
        return;
      }
      if (isLastStep) {
        const invalidStepIndexes = [];
        for (let index = 0; index < dialogSteps.length - 1; index += 1) {
          const stepHandler = handlersRef.current[index];
          if (!stepHandler) {
            continue;
          }
          const isStepValid = yield stepHandler();
          if (!isStepValid) {
            invalidStepIndexes.push(index);
          }
        }
        if (invalidStepIndexes.length > 0) {
          setInvalidSteps(invalidStepIndexes);
          invalidStepIndexes.forEach(index => {
            setStepCompleted(index, false);
          });
          goToStep(invalidStepIndexes[0], {
            keepToast: true
          });
          showStepInvalidToast();
          return;
        }
        setInvalidSteps([]);
        const submitResult = yield handler();
        if (submitResult) {
          onClose();
        }
        return;
      }
      const result = yield handler();
      if (!result) {
        setInvalidSteps([activeStep]);
        setStepCompleted(activeStep, false);
        showStepInvalidToast();
        return;
      }
      setInvalidSteps([]);
      setStepCompleted(activeStep, true);
      if (allStepsCompleted) {
        const lastHandler = handlersRef.current[dialogSteps.length - 1];
        if (lastHandler) {
          const submitResult = yield lastHandler();
          if (submitResult) {
            onClose();
          }
        }
        return;
      }
      goToStep(activeStep + 1);
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  }), [activeStep, dialogSteps.length, linear, onClose, setLoading, goToStep, isLastStep, allStepsCompleted, showStepInvalidToast, setStepCompleted, waitForProceedHandler, showToast]);
  const handleRequestClose = useCallback(() => {
    if (confirmClose === null || confirmClose === void 0 ? void 0 : confirmClose.isDirty()) {
      setConfirmVisible(true);
    } else {
      onClose();
    }
  }, [confirmClose, onClose]);
  const dialogClassName = clsx('dialog-stepper', size, propsDialogClassName);
  const closeButton = buttons === null || buttons === void 0 ? void 0 : buttons.closeButton;
  const submitLabel = (_a = buttons === null || buttons === void 0 ? void 0 : buttons.submitLabel) !== null && _a !== void 0 ? _a : 'Submit';
  const submitDisabled = typeof (buttons === null || buttons === void 0 ? void 0 : buttons.submitDisabled) === 'function' ? buttons.submitDisabled() : Boolean(buttons === null || buttons === void 0 ? void 0 : buttons.submitDisabled);
  const shouldShowStepperNavigation = dialogSteps.length > 1;
  const stepperNavigation = shouldShowStepperNavigation ? jsx(Stepper, {
    variant: "pill",
    className: clsx(linear && primaryDisabled && 'ics-stepper-locked'),
    pt: {
      nav: {
        className: 'mb-3',
        onClickCapture: handleStepHeaderClickCapture
      },
      panelContainer: {
        style: {
          display: 'none'
        }
      }
    },
    activeStep: activeStep,
    onChangeStep: e => {
      if (!linear) {
        goToStep(e.index);
        return;
      }
      const canGoBack = e.index <= maxReachedStep;
      const canGoForward = e.index === activeStep + 1 && !primaryDisabled;
      if (canGoBack || canGoForward) {
        if (e.index !== activeStep && disabledMap[activeStep] === false) {
          setStepCompleted(activeStep, true);
        }
        goToStep(e.index);
      }
    },
    linear: false,
    children: dialogSteps.map((step, index) => {
      const headerState = getStepHeaderState(index);
      const separatorState = getSeparatorState(index);
      return jsx(StepperPanel, {
        header: "".concat(index + 1, ". ").concat(step.title),
        pt: {
          header: {
            'data-ics-step-index': index,
            'data-ics-reached': headerState.reached,
            'data-ics-completed': headerState.completed,
            'data-ics-next-accessible': headerState.nextAccessible,
            'data-ics-invalid': headerState.invalid,
            'data-ics-disabled': headerState.disabled
          },
          action: {
            tabIndex: headerState.canInteract ? 0 : -1,
            'aria-disabled': headerState.canInteract ? 'false' : 'true',
            disabled: !headerState.canInteract
          },
          separator: {
            'data-ics-reached': separatorState.reached,
            'data-ics-next-accessible': separatorState.nextAccessible
          }
        }
      }, index);
    })
  }) : null;
  function ActionButtons() {
    var _a;
    const close = useDialogClose();
    const showSubmit = !linear || isLastStep || allStepsCompleted;
    return jsxs(Fragment, {
      children: [jsx(Button, {
        loading: loading,
        disabled: loading || showSubmit && submitDisabled,
        label: showSubmit ? submitLabel : 'Next',
        icon: !showSubmit && jsx(Icon, {
          name: "arrow_right_alt"
        }),
        iconPos: "right",
        onClick: handleProceed
      }), jsx(Button$1, {
        disabled: loading,
        label: (_a = closeButton === null || closeButton === void 0 ? void 0 : closeButton.label) !== null && _a !== void 0 ? _a : 'Close',
        severity: "secondary",
        onClick: () => {
          if (closeButton === null || closeButton === void 0 ? void 0 : closeButton.callback) {
            closeButton.callback();
          }
          if (!(closeButton === null || closeButton === void 0 ? void 0 : closeButton.overrideDefault)) {
            close();
          }
        }
      })]
    });
  }
  return jsxs(StepperDialogStepsContext.Provider, {
    value: {
      setSteps: setDialogSteps
    },
    children: [jsx(Toast, {
      ref: toastRef,
      position: "top-center",
      baseZIndex: 10000
    }), jsxs(DialogWithDoubleEscape, {
      onClose: onClose,
      onBeforeClose: confirmClose ? handleRequestClose : undefined,
      doubleEscapeEnabled: !confirmVisible,
      className: dialogClassName,
      header: jsx(HeaderContent, {}),
      footer: size !== DialogSize.sm ? undefined : jsx(FooterContent, {}),
      pt: pt,
      children: [stepperNavigation, jsx(Wrapper, {
        children: jsx("div", {
          children: dialogSteps.map((step, index) => jsx("div", {
            style: activeStep !== index ? {
              display: 'none'
            } : undefined,
            children: jsx(StepIndexProvider, {
              index: index,
              children: jsx(StepContentWrapper, {
                content: step.content,
                index: index,
                registerProceed: registerProceed,
                setPrimaryDisabled: setPrimaryDisabled,
                showToast: showToast
              })
            })
          }, index))
        })
      })]
    }), confirmClose && jsx(CloseConfirmationDialog, {
      visible: confirmVisible,
      config: confirmClose,
      onCancel: () => setConfirmVisible(false),
      onConfirm: () => {
        setConfirmVisible(false);
        onClose();
      }
    })]
  });
  function HeaderContent() {
    return jsxs("div", {
      className: "flex flex-row justify-content-between align-items-center w-full",
      children: [jsxs("div", {
        className: "flex flex-column gap-1",
        children: [jsx("div", {
          className: "flex align-items-center",
          children: title
        }), subtitle ? jsx("div", {
          className: "text-secondary text-sm font-normal text-transform-none",
          children: subtitle
        }) : null]
      }), jsx("div", {
        className: "flex flex-row gap-2",
        children: jsx(ActionButtons, {})
      })]
    });
  }
  function FooterContent() {
    return jsx("div", {
      className: "flex flex-row gap-2",
      children: jsx(ActionButtons, {})
    });
  }
}
function StepContentWrapper(_ref6) {
  let {
    content,
    index,
    registerProceed,
    setPrimaryDisabled,
    showToast
  } = _ref6;
  const contextValue = useMemo(() => ({
    registerProceed: handler => registerProceed(handler, index),
    setPrimaryDisabled: (disabled, options) => setPrimaryDisabled(disabled, index, options),
    showToast
  }), [index, registerProceed, setPrimaryDisabled, showToast]);
  return jsx(StepperDialogContext.Provider, {
    value: contextValue,
    children: content
  });
}

export { StepperDialog };
